import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  useColorModeValue as mode,
  useDisclosure,
} from '@chakra-ui/react';
import { conversationAction, documentAction } from 'actions';
import FilePreview from 'components/FilePreview';
import configs from 'configs';
import { HTTP_CODE_RESPONSE, TEMPORARY } from 'helpers/constant';
import { VIEW_MODE } from 'helpers/enum';
import { addTokenURL, getFileExtension } from 'helpers/file';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { UploadService } from 'services/upload/upload.service';
import { Citation, Conversation } from 'types/reducers/conversation';
import { getStorageReadBlobToken } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

import Chat from './Chat';
import ConversationsList from './ConversationsList';

const Ask = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { conversations, selectedCitation, viewMode, processingStreamingChunkStatus } = useSelector(
    (state) => state.conversations
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isFileValid, setIsFileValid] = useState(true);

  const { isOpen, onOpen: onOpenSidebar, onClose: onCloseSidebar } = useDisclosure();

  const drawerBackgroundColor = mode('white.500', '#424242');

  const newChat = useMemo(
    () => ({
      id: uuidv4(),
      type: TEMPORARY,
      name: t('common.newChat'),
      last_updated: new Date().toISOString(),
    }),
    [t, i18n.language]
  );

  useEffect(() => {
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadData = () => {
    setIsLoading(true);
    dispatch(conversationAction.resetData());
    dispatch(conversationAction.getConversations());
    setIsLoading(false);
  };

  const onChangeConversation = (item: Conversation) => {
    if (processingStreamingChunkStatus) {
      return;
    }
    dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT));
    dispatch(
      conversationAction.updateCurrentConversation({
        conversation_id: item?.id,
        type: item?.type,
      })
    );
  };

  const addNewConversation = () => {
    if (processingStreamingChunkStatus) {
      return;
    }

    const temporaryConversation = conversations.data.find(
      (conversation) => conversation.type === TEMPORARY
    );
    if (temporaryConversation) {
      dispatch(
        conversationAction.updateCurrentConversation({
          conversation_id: temporaryConversation.id,
          type: temporaryConversation?.type,
        })
      );
      return;
    }

    dispatch(conversationAction.addNewConversation(newChat));
    onChangeConversation(newChat);
  };

  const checkReadTokenValid = (token: string | null) => {
    if (!token) {
      return false;
    }
    const searchParams = new URLSearchParams(token);
    const expiredTime = new Date(searchParams.get('se') as string).getTime();
    const currentTime = new Date().getTime();
    const offsetRefreshTime = configs.setting.TIMEOUT_REFRESH_SAS_TOKEN_IN_MS;

    return expiredTime > currentTime + offsetRefreshTime;
  };

  const onOpenFile = (citation: Citation) => {
    readFile(citation);
  };

  const readFile = (citation: Citation) => {
    if (!checkReadTokenValid(getStorageReadBlobToken())) {
      dispatch(documentAction.getReadToken());
    }
    fetchData(citation);
  };

  const fetchData = (citation: Citation) => {
    const fileUrl = addTokenURL(citation?.file_url, getStorageReadBlobToken());

    dispatch(
      conversationAction.updateSelectedCitation({
        ...citation,
        file_url: fileUrl,
        fileType: getFileExtension(citation.file_url),
        page: 1,
      })
    );

    (async () => {
      try {
        const res = await UploadService.fetchHeadFile(fileUrl);
        if (
          res.statusText !== 'OK' &&
          (res.status === HTTP_CODE_RESPONSE.UNAUTHORIZED ||
            res.status === HTTP_CODE_RESPONSE.NO_SAS_TOKEN)
        ) {
          readFile(citation);
        } else if (res.status === HTTP_CODE_RESPONSE.NOT_FOUND) {
          setIsFileValid(false);
          dispatch(conversationAction.updateViewMode(VIEW_MODE.PREVIEW_FILE));
        } else {
          setIsFileValid(true);
          dispatch(conversationAction.updateViewMode(VIEW_MODE.PREVIEW_FILE));
        }
      } catch (error) {
        console.log(error);
      }
    })();
  };

  return (
    <>
      <Flex gap={4} w={{ base: '100%', md: 'auto' }}>
        {isLoading ? (
          <Box w='100vw' h='100vh' bg='bg_main_container'>
            <Spinner />
          </Box>
        ) : (
          <>
            <Box display={{ base: 'none', md: 'block' }}>
              <ConversationsList
                onAddNew={addNewConversation}
                onChange={onChangeConversation}
                onCloseSidebar={onCloseSidebar}
              />
            </Box>
            <Flex
              pos={'relative'}
              justify={'center'}
              w='100%'
              h={{ base: 'calc(100vh - 100px)', lg: 'calc(100vh - 140px)' }}
            >
              <Chat
                onOpenFile={onOpenFile}
                onAddNew={addNewConversation}
                onOpenSidebar={onOpenSidebar}
              />
              {viewMode === VIEW_MODE.PREVIEW_FILE && selectedCitation?.file_url && (
                <Box
                  pos={{ base: 'absolute', md: 'static' }}
                  zIndex={{ base: 9999, md: 'initial' }}
                  top={{ base: 0, md: 'initial' }}
                  right={{ base: 0, md: 'initial' }}
                  bottom={{ base: 0, md: 'initial' }}
                  left={{ base: -10, md: 'initial' }}
                  w={{ base: '100vw', md: '50%' }}
                >
                  <FilePreview
                    onClose={() => dispatch(conversationAction.updateViewMode(VIEW_MODE.CHAT))}
                    data={selectedCitation}
                    isValid={isFileValid}
                  />
                </Box>
              )}
            </Flex>
            <Drawer isOpen={isOpen} onClose={onCloseSidebar} placement='left'>
              <DrawerOverlay />
              <DrawerContent bgColor={drawerBackgroundColor}>
                <DrawerCloseButton />
                <DrawerHeader>
                  <Button>{t('chat.conversations')}</Button>
                </DrawerHeader>

                <DrawerBody>
                  <Box>
                    <ConversationsList
                      onAddNew={addNewConversation}
                      onChange={onChangeConversation}
                      onCloseSidebar={onCloseSidebar}
                    />
                  </Box>
                </DrawerBody>
              </DrawerContent>
            </Drawer>
          </>
        )}
      </Flex>
    </>
  );
};

export default Ask;
